import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import PropTypes from "prop-types";

import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Icon from "@mui/material/Icon";
import Avatar from "@mui/material/Avatar";
import LinearProgress from "@mui/material/LinearProgress";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import { api, get, put } from "lib/api";
import InfoCard from "mobile/components/InfoCard";
import MobileTopBar from "mobile/components/MobileTopBar";
import { IosCard, IosPage, iosColors } from "mobile/components/IosParity";

const normalize = (d) =>
  d?.attributes ? { id: d.id, documentId: d.documentId || d.id, ...d.attributes } : { ...d, documentId: d.documentId || d.id };

const normalizeRelation = (value) => {
  if (!value) return null;
  if (value?.data) return normalizeRelation(value.data);
  return value?.data ? normalize(value.data) : normalize(value);
};

const formatMoney = (value) => {
  if (value === "" || value === null || value === undefined) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "—";
};

const formatNumber = (value) => {
  if (value === "" || value === null || value === undefined) return "—";
  const n = Number(value);
  return Number.isFinite(n) ? n.toLocaleString(undefined, { maximumFractionDigits: 3 }) : "—";
};

const pretty = (value, fallback = "—") => {
  const text = String(value || "").trim();
  if (!text) return fallback;
  return text.replaceAll("_", " ").replace(/\b\w/g, (m) => m.toUpperCase());
};

const typeLabel = (value) =>
  ({
    FG: "Finished Goods",
    BI: "Bulk Items",
    CORE: "Core",
    BANK: "Bank",
    OVERHEAD: "Overhead",
    FIXED_COSTS: "Fixed Costs",
  }[String(value || "").toUpperCase()] || String(value || "—"));

const getImageUrl = (image) => {
  const row = normalizeRelation(image);
  const url = row?.url || row?.formats?.thumbnail?.url || "";
  if (!url) return "";
  if (/^https?:\/\//i.test(url)) return url;
  return url.startsWith("/") ? url : `/${url}`;
};

const uploadImages = async (files) => {
  const rows = Array.from(files || []).filter(Boolean);
  if (!rows.length) return [];
  const formData = new FormData();
  rows.forEach((file) => formData.append("files", file));
  const response = await api.post("/api/upload", formData);
  return (Array.isArray(response?.data) ? response.data : []).map((row) => Number(row?.id || 0)).filter((id) => Number.isFinite(id) && id > 0);
};

const dateTimeText = (value) => {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const primaryActionSx = {
  bgcolor: "#B54708",
  color: "#fff",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  boxShadow: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "#953A05", boxShadow: "none" },
  "&.Mui-disabled": { bgcolor: "rgba(181,71,8,0.18)", color: "rgba(15,15,18,0.44)" },
};

const secondaryActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  borderColor: "rgba(181,71,8,0.38)",
  borderRadius: 1,
  minHeight: 44,
  fontWeight: 900,
  textTransform: "none",
  whiteSpace: "normal",
  overflowWrap: "anywhere",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)", borderColor: "rgba(181,71,8,0.52)" },
};

const iconActionSx = {
  bgcolor: "rgba(255,106,0,0.07)",
  color: "#8F3700",
  border: "1px solid rgba(181,71,8,0.24)",
  "&:hover": { bgcolor: "rgba(255,106,0,0.12)" },
};

const stickyFooterSx = {
  position: "fixed",
  left: 0,
  right: 0,
  bottom: 0,
  zIndex: 20,
  px: 1,
  pt: 0.8,
  pb: "calc(env(safe-area-inset-bottom) + 0.8rem)",
  bgcolor: "rgba(247,248,250,0.96)",
  borderTop: `1px solid ${iosColors.line}`,
  backdropFilter: "blur(14px)",
};

export default function InventoryDetailMobile() {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [editOpen, setEditOpen] = useState(false);
  const [txOpen, setTxOpen] = useState(false);
  const [notice, setNotice] = useState(null);
  const [imageFiles, setImageFiles] = useState([]);
  const [editDraft, setEditDraft] = useState({
    sku: "",
    name: "",
    description: "",
    itemType: "FG",
    productFamily: "",
    status: "",
    location: "",
    quantityOnHand: "",
    quantityReserved: "",
    lotNumber: "",
    serialNumber: "",
    shippingWeightKg: "",
    packageLengthCm: "",
    packageWidthCm: "",
    packageHeightCm: "",
    unitCost: "",
    unitPrice: "",
    coreCost: "",
    coreCharge: "",
    priceRetail: "",
    priceWholesale: "",
    priceBulk: "",
    satProductKey: "",
    satUnitKey: "",
    satUnitName: "",
    taxObject: "",
    mxTaxes: "",
    isWhiteLabel: false,
    whiteLabelName: "",
    marketingDescription: "",
    unitSpecificDetails: "",
    notes: "",
  });

  const mergeEditDraft = useCallback((partial) => {
    setEditDraft((previous) => ({ ...previous, ...partial }));
  }, []);

  const { data, isLoading, error } = useQuery({
    queryKey: ["m-inventory-item", documentId],
    queryFn: () =>
      get(`/api/inventory-items/${documentId}`, {
        populate: {
          images: true,
          whiteLabelLogo: true,
          part: true,
          coreItem: true,
          bankItem: true,
          parentOverhead: true,
          fixedCosts: true,
          overheadItems: { populate: { inventoryItem: true } },
          transmissions: { fields: ["documentId", "name", "manufacturer", "type", "family"] },
          whiteLabelCustomer: true,
          whiteLabelCustomers: true,
          whiteLabelEntries: { populate: { customer: true, logo: true } },
          vendorEntries: { populate: { vendor: true } },
        },
      }),
    enabled: !!documentId,
    staleTime: 10_000,
  });

  const item = useMemo(() => (data?.data ? normalize(data.data) : null), [data]);
  const images = useMemo(() => item?.images?.data || item?.images || [], [item]);
  const heroUrl = useMemo(() => getImageUrl(images?.[0]) || null, [images]);
  const invRefForTx = item?.documentId || documentId;
  const txQuery = useQuery({
    queryKey: ["m-inventory-item-transactions", invRefForTx],
    queryFn: () => get(`/api/inventory-items/${invRefForTx}/transactions`, { limit: 250 }),
    enabled: txOpen && !!invRefForTx,
    staleTime: 0,
  });
  const txRows = Array.isArray(txQuery.data?.transactions) ? txQuery.data.transactions : [];

  useEffect(() => {
    if (!item) return;
    const priceLevels = item.priceLevels && typeof item.priceLevels === "object" ? item.priceLevels : {};
    setEditDraft({
      sku: item.sku || "",
      name: item.name || "",
      description: item.description || "",
      itemType: item.itemType || "FG",
      productFamily: item.productFamily || "",
      status: item.status || "",
      location: item.location || "",
      quantityOnHand: item.quantityOnHand ?? "",
      quantityReserved: item.quantityReserved ?? "",
      lotNumber: item.lotNumber || "",
      serialNumber: item.serialNumber || "",
      shippingWeightKg: item.shippingWeightKg ?? "",
      packageLengthCm: item.packageLengthCm ?? "",
      packageWidthCm: item.packageWidthCm ?? "",
      packageHeightCm: item.packageHeightCm ?? "",
      unitCost: item.unitCost ?? "",
      unitPrice: item.unitPrice ?? "",
      coreCost: item.coreCost ?? "",
      coreCharge: item.coreCharge ?? "",
      priceRetail: priceLevels.retail ?? "",
      priceWholesale: priceLevels.wholesale ?? "",
      priceBulk: priceLevels.bulk ?? "",
      satProductKey: item.satProductKey || "",
      satUnitKey: item.satUnitKey || "",
      satUnitName: item.satUnitName || "",
      taxObject: item.taxObject || "",
      mxTaxes: item.mxTaxes ? JSON.stringify(item.mxTaxes, null, 2) : "",
      isWhiteLabel: !!item.isWhiteLabel,
      whiteLabelName: item.whiteLabelName || "",
      marketingDescription: item.marketingDescription || "",
      unitSpecificDetails: item.unitSpecificDetails || "",
      notes: item.notes || "",
    });
    setImageFiles([]);
  }, [item, editOpen]);

  const updateItemMutation = useMutation({
    mutationFn: async (payload) => {
      const existingImageIds = images.map((image) => Number(image?.id || image?.documentId || 0)).filter((imageId) => Number.isFinite(imageId) && imageId > 0);
      const uploadedImageIds = await uploadImages(imageFiles);
      return put(`/api/inventory-items/${item?.documentId || documentId}`, {
        data: {
          ...payload,
          ...(uploadedImageIds.length ? { images: [...existingImageIds, ...uploadedImageIds] } : {}),
        },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["m-inventory-item", documentId] });
      qc.invalidateQueries({ queryKey: ["m-inventory"] });
      setEditOpen(false);
      setImageFiles([]);
      setNotice({ type: "success", text: "Inventory item saved." });
    },
    onError: (mutationError) => {
      setNotice({ type: "error", text: mutationError?.response?.data?.error?.message || mutationError?.message || "Unable to save inventory item." });
    },
  });

  if (isLoading) {
    return (
      <IosPage>
        <LinearProgress sx={{ borderRadius: 1 }} />
      </IosPage>
    );
  }

  if (error || !item) {
    return (
      <IosPage>
        <MobileTopBar title="Inventory" subtitle="Back to list" onBack={() => navigate("/m/inventory")} />
        <IosCard sx={{ mt: 1 }}>
          <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900 }}>Item not found</Typography>
          <Typography sx={{ color: iosColors.muted, fontSize: 13, mt: 0.4 }}>
            This inventory item could not be loaded.
          </Typography>
        </IosCard>
      </IosPage>
    );
  }

  const qoh = Number(item.quantityOnHand ?? 0);
  const reserved = Number(item.quantityReserved ?? 0);
  const coreItem = normalizeRelation(item.coreItem);
  const bankItem = normalizeRelation(item.bankItem);
  const parentOverhead = normalizeRelation(item.parentOverhead);
  const part = normalizeRelation(item.part);
  const whiteLabelCustomer = normalizeRelation(item.whiteLabelCustomer);
  const whiteLabelCustomers = Array.isArray(item.whiteLabelCustomers?.data || item.whiteLabelCustomers) ? item.whiteLabelCustomers?.data || item.whiteLabelCustomers : [];
  const whiteLabelEntries = Array.isArray(item.whiteLabelEntries) ? item.whiteLabelEntries : [];
  const transmissions = Array.isArray(item.transmissions?.data || item.transmissions) ? item.transmissions?.data || item.transmissions : [];
  const vendorEntries = Array.isArray(item.vendorEntries?.data || item.vendorEntries) ? item.vendorEntries?.data || item.vendorEntries : [];
  const overheadItems = Array.isArray(item.overheadItems) ? item.overheadItems : [];
  const fixedCosts = Array.isArray(item.fixedCosts?.data || item.fixedCosts) ? item.fixedCosts?.data || item.fixedCosts : [];
  const priceLevels = item.priceLevels && typeof item.priceLevels === "object" ? item.priceLevels : {};
  const itemType = String(item.itemType || "").toUpperCase();
  const relationRows = [
    coreItem?.documentId ? { label: "Core item", value: `${coreItem.sku || "Core"}${coreItem.name ? ` • ${coreItem.name}` : ""}`, path: `/m/inventory/${coreItem.documentId}` } : null,
    bankItem?.documentId ? { label: "Bank item", value: `${bankItem.sku || "Bank"}${bankItem.name ? ` • ${bankItem.name}` : ""}`, path: `/m/inventory/${bankItem.documentId}` } : null,
    parentOverhead?.documentId ? { label: "Parent overhead", value: `${parentOverhead.sku || "Overhead"}${parentOverhead.name ? ` • ${parentOverhead.name}` : ""}`, path: `/m/inventory/${parentOverhead.documentId}` } : null,
    part ? { label: "Part", value: part.partNumber || part.name || part.sku || "Linked part" } : null,
    whiteLabelCustomer ? { label: "White label", value: whiteLabelCustomer.name || whiteLabelCustomer.companyName || whiteLabelCustomer.email || "Customer" } : null,
  ].filter(Boolean);

  return (
    <IosPage>
      <MobileTopBar
        title="Inventory"
        subtitle="Back to list"
        onBack={() => navigate("/m/inventory")}
        right={
          <Stack direction="row" spacing={0.6} sx={{ flexWrap: "wrap", justifyContent: "flex-end", rowGap: 0.5 }}>
            {item.itemType ? (
              <Chip size="small" label={typeLabel(item.itemType)} sx={{ bgcolor: "#fff", border: `1px solid ${iosColors.line}`, fontWeight: 850 }} />
            ) : null}
            {item.status ? (
              <Chip size="small" label={String(item.status)} sx={{ bgcolor: "#fff", border: `1px solid ${iosColors.line}`, fontWeight: 850 }} />
            ) : null}
            <IconButton aria-label="Transactions" size="small" onClick={() => setTxOpen(true)} sx={iconActionSx}>
              <Icon fontSize="small">history</Icon>
            </IconButton>
            <IconButton aria-label="Edit item" size="small" onClick={() => setEditOpen(true)} sx={iconActionSx}>
              <Icon fontSize="small">edit</Icon>
            </IconButton>
          </Stack>
        }
      />

      <Stack spacing={0.9} sx={{ mt: 0.8, pb: 1 }}>
        {notice ? (
          <IosCard sx={{ borderColor: notice.type === "success" ? "rgba(20,122,92,0.35)" : "rgba(215,38,30,0.35)" }}>
            <Typography sx={{ color: notice.type === "success" ? iosColors.green : iosColors.red, fontSize: 13, fontWeight: 850 }}>
              {notice.text}
            </Typography>
          </IosCard>
        ) : null}

        <IosCard>
          <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>
            Inventory item
          </Typography>
          <Typography sx={{ color: iosColors.ink, fontSize: 20, fontWeight: 950, lineHeight: 1.12, mt: 0.25 }}>
            {item.sku || "Inventory Item"}
          </Typography>
          <Typography sx={{ color: iosColors.muted, fontSize: 13, lineHeight: 1.32, mt: 0.3 }}>
            {item.name || "No name"}
          </Typography>
          <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
            <Chip size="small" label={typeLabel(item.itemType)} sx={{ bgcolor: "rgba(255,106,0,0.07)", color: "#8F3700", border: `1px solid ${iosColors.line}`, fontWeight: 850 }} />
            <Chip size="small" label={pretty(item.status || "available")} sx={{ bgcolor: "#fff", color: iosColors.muted, border: `1px solid ${iosColors.line}`, fontWeight: 850 }} />
            {item.productFamily ? <Chip size="small" label={pretty(item.productFamily)} sx={{ bgcolor: "#fff", color: iosColors.muted, border: `1px solid ${iosColors.line}`, fontWeight: 850 }} /> : null}
          </Stack>
        </IosCard>

        {heroUrl ? (
          <IosCard sx={{ p: 0, overflow: "hidden" }}>
            <Box
              component="img"
              src={heroUrl}
              alt={item.name || item.sku || "Item"}
              sx={{ display: "block", width: "100%", height: 178, objectFit: "cover" }}
            />
          </IosCard>
        ) : null}

        {images.length > 1 ? (
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.8 }}>Images</Typography>
            <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
              {images.map((image, index) => {
                const url = getImageUrl(image);
                return (
                  <Avatar
                    key={String(image?.id || image?.documentId || index)}
                    src={url || undefined}
                    variant="rounded"
                    sx={{ width: 66, height: 66, borderRadius: 1, border: `1px solid ${index === 0 ? iosColors.orange : iosColors.line}` }}
                  >
                    <Icon>image</Icon>
                  </Avatar>
                );
              })}
            </Stack>
          </IosCard>
        ) : null}

        <InfoCard
          title="Stock"
          rows={[
            { label: "On hand", value: Number.isFinite(qoh) ? qoh : 0 },
            { label: "Reserved", value: Number.isFinite(reserved) ? reserved : 0 },
            { label: "Available", value: Number.isFinite(qoh - reserved) ? qoh - reserved : 0 },
            { label: "Location", value: item.location || "—" },
            { label: "Lot", value: item.lotNumber || "—" },
            { label: "Serial", value: item.serialNumber || "—" },
          ]}
          footer={
            <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.3 }}>
              Use the edit button to adjust stock, location, and pricing.
            </Typography>
          }
        />

        <InfoCard
          title="Pricing"
          rows={[
            { label: "Unit price", value: formatMoney(item.unitPrice) },
            { label: "Unit cost", value: formatMoney(item.unitCost) },
            { label: "Retail", value: formatMoney(priceLevels.retail ?? item.unitPrice) },
            { label: "Wholesale", value: formatMoney(priceLevels.wholesale) },
            { label: "Bulk", value: formatMoney(priceLevels.bulk) },
            { label: "Core cost", value: formatMoney(item.coreCost) },
            { label: "Core charge", value: formatMoney(item.coreCharge) },
          ]}
        />

        {itemType !== "OVERHEAD" && itemType !== "FIXED_COSTS" ? (
          <InfoCard
            title="Shipping package"
            rows={[
              { label: "Weight", value: item.shippingWeightKg != null ? `${formatNumber(item.shippingWeightKg)} kg` : item.shippingWeightOz != null ? `${formatNumber(item.shippingWeightOz)} oz` : "—" },
              { label: "Length", value: item.packageLengthCm != null ? `${formatNumber(item.packageLengthCm)} cm` : item.packageLengthIn != null ? `${formatNumber(item.packageLengthIn)} in` : "—" },
              { label: "Width", value: item.packageWidthCm != null ? `${formatNumber(item.packageWidthCm)} cm` : item.packageWidthIn != null ? `${formatNumber(item.packageWidthIn)} in` : "—" },
              { label: "Height", value: item.packageHeightCm != null ? `${formatNumber(item.packageHeightCm)} cm` : item.packageHeightIn != null ? `${formatNumber(item.packageHeightIn)} in` : "—" },
            ]}
          />
        ) : null}

        {relationRows.length ? (
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.8 }}>Linked records</Typography>
            <Stack spacing={0.65}>
              {relationRows.map((row) =>
                row.path ? (
                  <ButtonBase key={row.label} onClick={() => navigate(row.path)} sx={{ display: "block", width: "100%", textAlign: "left", borderRadius: 1 }}>
                    <Box sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                      <Stack direction="row" spacing={0.75} alignItems="flex-start">
                        <Box sx={{ minWidth: 0, flex: 1 }}>
                          <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>{row.label}</Typography>
                          <Typography sx={{ color: iosColors.ink, fontSize: 13, fontWeight: 850, mt: 0.25 }}>{row.value}</Typography>
                        </Box>
                        <Icon sx={{ color: iosColors.subtle, fontSize: 18 }}>chevron_right</Icon>
                      </Stack>
                    </Box>
                  </ButtonBase>
                ) : (
                  <Box key={row.label} sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                    <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>{row.label}</Typography>
                    <Typography sx={{ color: iosColors.ink, fontSize: 13, fontWeight: 850, mt: 0.25 }}>{row.value}</Typography>
                  </Box>
                )
              )}
            </Stack>
          </IosCard>
        ) : null}

        {transmissions.length ? (
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.8 }}>Transmissions</Typography>
            <Stack direction="row" spacing={0.45} flexWrap="wrap" useFlexGap>
              {transmissions.map((entry, index) => {
                const row = normalize(entry);
                return (
                  <Chip
                    key={row.documentId || row.id || index}
                    size="small"
                    label={[row.name, row.manufacturer].filter(Boolean).join(" • ") || "Transmission"}
                    sx={{ bgcolor: "#fff", color: iosColors.muted, border: `1px solid ${iosColors.line}`, fontWeight: 850 }}
                  />
                );
              })}
            </Stack>
          </IosCard>
        ) : null}

        {(item.satProductKey || item.satUnitKey || item.satUnitName || item.taxObject || item.mxTaxes) ? (
          <InfoCard
            title="MX CFDI / SAT"
            rows={[
              { label: "SAT product", value: item.satProductKey || "—" },
              { label: "SAT unit key", value: item.satUnitKey || "—" },
              { label: "SAT unit name", value: item.satUnitName || "—" },
              { label: "Tax object", value: item.taxObject || "—" },
              { label: "MX taxes", value: item.mxTaxes ? JSON.stringify(item.mxTaxes) : "—" },
            ]}
          />
        ) : null}

        {item.isWhiteLabel || whiteLabelCustomer || whiteLabelCustomers.length || whiteLabelEntries.length ? (
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.8 }}>White label</Typography>
            <Stack spacing={0.65}>
              <InfoLine label="Enabled" value={item.isWhiteLabel ? "Yes" : "No"} />
              <InfoLine label="Name" value={item.whiteLabelName || "—"} />
              <InfoLine label="Primary customer" value={whiteLabelCustomer?.name || whiteLabelCustomer?.companyName || whiteLabelCustomer?.email || "—"} />
              {whiteLabelCustomers.length ? <InfoLine label="Customers" value={whiteLabelCustomers.map((entry) => normalize(entry)?.name || normalize(entry)?.email || "Customer").join(", ")} /> : null}
              {whiteLabelEntries.length ? (
                <Stack spacing={0.45}>
                  <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>Entries</Typography>
                  {whiteLabelEntries.map((entry, index) => {
                    const customer = normalizeRelation(entry?.customer);
                    return (
                      <Box key={entry?.id || index} sx={{ p: 0.85, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                        <Typography sx={{ color: iosColors.ink, fontSize: 12.8, fontWeight: 850 }}>
                          {entry?.name || customer?.name || customer?.email || "White label entry"}
                        </Typography>
                      </Box>
                    );
                  })}
                </Stack>
              ) : null}
            </Stack>
          </IosCard>
        ) : null}

        {itemType === "OVERHEAD" || itemType === "FIXED_COSTS" ? (
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.8 }}>Overhead / Fixed Costs</Typography>
            <Stack spacing={0.65}>
              {parentOverhead ? <InfoLine label="Parent overhead" value={`${parentOverhead.sku || ""}${parentOverhead.name ? ` • ${parentOverhead.name}` : ""}`.trim() || "—"} /> : null}
              <InfoLine label="Configured overhead items" value={overheadItems.length} />
              <InfoLine label="Linked fixed costs" value={fixedCosts.length} />
              {overheadItems.map((entry, index) => {
                const inventoryItem = normalizeRelation(entry?.inventoryItem);
                return (
                  <Box key={entry?.id || index} sx={{ p: 0.85, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                    <Typography sx={{ color: iosColors.ink, fontSize: 12.8, fontWeight: 850 }}>
                      {inventoryItem?.sku || "Overhead item"}{inventoryItem?.name ? ` • ${inventoryItem.name}` : ""}
                    </Typography>
                    <Typography sx={{ color: iosColors.muted, fontSize: 12.2, mt: 0.2 }}>
                      {pretty(entry?.costType || "direct")} • {formatMoney(entry?.amount)}
                    </Typography>
                  </Box>
                );
              })}
              {fixedCosts.map((entry, index) => {
                const row = normalize(entry);
                return (
                  <Box key={row.documentId || row.id || index} sx={{ p: 0.85, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                    <Typography sx={{ color: iosColors.ink, fontSize: 12.8, fontWeight: 850 }}>
                      {row.sku || "Fixed cost"}{row.name ? ` • ${row.name}` : ""}
                    </Typography>
                    <Typography sx={{ color: iosColors.muted, fontSize: 12.2, mt: 0.2 }}>{formatMoney(row.unitCost)}</Typography>
                  </Box>
                );
              })}
            </Stack>
          </IosCard>
        ) : null}

        {vendorEntries.length ? (
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.8 }}>Vendors</Typography>
            <Stack spacing={0.75}>
              {vendorEntries.map((entry, idx) => {
                const row = normalize(entry);
                const vendor = normalizeRelation(row.vendor);
                return (
                  <Box key={row.id || idx} sx={{ p: 1, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                    <Typography sx={{ color: iosColors.ink, fontSize: 14, fontWeight: 900 }}>
                      {vendor?.name || vendor?.companyName || row.vendorName || "Vendor"}{row.preferred ? " • Preferred" : ""}
                    </Typography>
                    <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.3, mt: 0.25 }}>
                      {[row.vendorPartNumber ? `Part ${row.vendorPartNumber}` : null, row.leadTimeDays ? `${row.leadTimeDays} day lead` : null, row.minStock ? `Min ${row.minStock}` : null]
                        .filter(Boolean)
                        .join(" • ") || "No vendor details"}
                    </Typography>
                  </Box>
                );
              })}
            </Stack>
          </IosCard>
        ) : null}

        {item.description || item.marketingDescription || item.unitSpecificDetails || item.notes || (Array.isArray(item.technicalSections) && item.technicalSections.length) ? (
          <IosCard>
            <Typography sx={{ color: iosColors.ink, fontSize: 15.5, fontWeight: 900, mb: 0.8 }}>Details</Typography>
            {item.description ? (
              <Box>
                <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>
                  Description
                </Typography>
                <Typography sx={{ color: iosColors.ink, fontSize: 13.25, lineHeight: 1.35, whiteSpace: "pre-wrap", mt: 0.25 }}>
                  {item.description}
                </Typography>
              </Box>
            ) : null}
            {item.marketingDescription ? (
              <Box>
                {item.description ? <Divider sx={{ my: 1.15 }} /> : null}
                <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>
                  Marketing description
                </Typography>
                <Typography sx={{ color: iosColors.ink, fontSize: 13.25, lineHeight: 1.35, whiteSpace: "pre-wrap", mt: 0.25 }}>
                  {item.marketingDescription}
                </Typography>
              </Box>
            ) : null}
            {item.unitSpecificDetails ? (
              <Box>
                {item.description || item.marketingDescription ? <Divider sx={{ my: 1.15 }} /> : null}
                <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>
                  Unit-specific details
                </Typography>
                <Typography sx={{ color: iosColors.ink, fontSize: 13.25, lineHeight: 1.35, whiteSpace: "pre-wrap", mt: 0.25 }}>
                  {item.unitSpecificDetails}
                </Typography>
              </Box>
            ) : null}
            {Array.isArray(item.technicalSections) && item.technicalSections.length ? (
              <Box>
                {item.description || item.marketingDescription || item.unitSpecificDetails ? <Divider sx={{ my: 1.15 }} /> : null}
                <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>
                  Technical sections
                </Typography>
                <Stack spacing={0.65} sx={{ mt: 0.55 }}>
                  {item.technicalSections.map((section, index) => (
                    <Box key={section?.id || index} sx={{ p: 0.85, border: `1px solid ${iosColors.line}`, borderRadius: 1, bgcolor: "#fff" }}>
                      <Typography sx={{ color: iosColors.ink, fontSize: 13, fontWeight: 900 }}>{section?.title || `Section ${index + 1}`}</Typography>
                      {section?.body ? (
                        <Typography sx={{ color: iosColors.muted, fontSize: 12.4, whiteSpace: "pre-wrap", mt: 0.25 }}>{section.body}</Typography>
                      ) : null}
                    </Box>
                  ))}
                </Stack>
              </Box>
            ) : null}
            {item.notes ? (
              <Box>
                {item.description || item.marketingDescription || item.unitSpecificDetails || (Array.isArray(item.technicalSections) && item.technicalSections.length) ? <Divider sx={{ my: 1.15 }} /> : null}
                <Typography sx={{ color: iosColors.muted, fontSize: 11.5, fontWeight: 900, textTransform: "uppercase" }}>
                  Notes
                </Typography>
                <Typography sx={{ color: iosColors.ink, fontSize: 13.25, lineHeight: 1.35, whiteSpace: "pre-wrap", mt: 0.25 }}>
                  {item.notes}
                </Typography>
              </Box>
            ) : null}
          </IosCard>
        ) : null}
      </Stack>

      <Dialog open={txOpen} onClose={() => setTxOpen(false)} fullScreen PaperProps={{ sx: { bgcolor: iosColors.canvas } }}>
        <Box sx={{ px: 1, pt: "env(safe-area-inset-top)", pb: 2, bgcolor: iosColors.canvas, minHeight: "100dvh" }}>
          <MobileTopBar title="Transactions" subtitle={item.sku || ""} onBack={() => setTxOpen(false)} />
          <Stack spacing={0.85} sx={{ mt: 1 }}>
            {txQuery.isLoading ? <LinearProgress sx={{ borderRadius: 1 }} /> : null}
            {txQuery.isError ? (
              <IosCard sx={{ borderColor: "rgba(215,38,30,0.35)" }}>
                <Typography sx={{ color: iosColors.red, fontSize: 13, fontWeight: 850 }}>Unable to load transactions.</Typography>
              </IosCard>
            ) : null}
            {!txQuery.isLoading && !txRows.length ? (
              <IosCard>
                <Typography sx={{ color: iosColors.muted, fontSize: 13 }}>No transactions found for this item.</Typography>
              </IosCard>
            ) : null}
            {txRows.map((row) => {
              const type = String(row?.type || "").toLowerCase();
              const qtyDelta = Number(row?.qtyDelta || 0);
              const workOrder = row?.workOrder || {};
              return (
                <IosCard key={row?.id || `${row?.occurredAt}-${row?.source}`}>
                  <Stack direction="row" spacing={0.8} alignItems="flex-start">
                    <Box
                      sx={{
                        width: 34,
                        height: 34,
                        borderRadius: 1,
                        bgcolor: qtyDelta >= 0 ? "rgba(20,122,92,0.08)" : "rgba(215,38,30,0.08)",
                        color: qtyDelta >= 0 ? iosColors.green : iosColors.red,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Icon fontSize="small">{qtyDelta >= 0 ? "add" : "remove"}</Icon>
                    </Box>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography sx={{ color: iosColors.ink, fontSize: 14, fontWeight: 900 }}>
                        {pretty(type || "movement")} • {qtyDelta >= 0 ? `+${qtyDelta}` : qtyDelta}
                      </Typography>
                      <Typography sx={{ color: iosColors.muted, fontSize: 12.5, lineHeight: 1.3, mt: 0.25 }}>
                        {[dateTimeText(row?.occurredAt), workOrder?.workOrderNumber || workOrder?.documentId || null, row?.source || null].filter(Boolean).join(" • ")}
                      </Typography>
                    </Box>
                  </Stack>
                </IosCard>
              );
            })}
          </Stack>
        </Box>
      </Dialog>

      <Dialog open={editOpen} onClose={() => setEditOpen(false)} fullScreen PaperProps={{ sx: { bgcolor: iosColors.canvas } }}>
        <Box sx={{ px: 1, pt: "env(safe-area-inset-top)", pb: 10, bgcolor: iosColors.canvas, minHeight: "100dvh" }}>
          <MobileTopBar title="Edit Inventory" subtitle={item.sku || ""} onBack={() => setEditOpen(false)} />

          <Stack spacing={0.9} sx={{ mt: 1 }}>
            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>Basic information</Typography>
              <Stack spacing={0.9}>
                <TextField fullWidth size="small" label="SKU" required value={editDraft.sku} onChange={(e) => mergeEditDraft({ sku: e.target.value })} />
                <TextField fullWidth size="small" label="Name" required value={editDraft.name} onChange={(e) => mergeEditDraft({ name: e.target.value })} />
                <TextField fullWidth size="small" label="Description" multiline minRows={3} value={editDraft.description} onChange={(e) => mergeEditDraft({ description: e.target.value })} />
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <FormControl fullWidth size="small">
                    <InputLabel>Item type</InputLabel>
                    <Select label="Item type" value={editDraft.itemType} onChange={(e) => mergeEditDraft({ itemType: e.target.value })}>
                      <MenuItem value="FG">Finished Goods (FG)</MenuItem>
                      <MenuItem value="BI">Bulk Items (BI)</MenuItem>
                      <MenuItem value="CORE">Core (CORE)</MenuItem>
                      <MenuItem value="BANK">Bank (-BANK)</MenuItem>
                      <MenuItem value="OVERHEAD">Overhead</MenuItem>
                      <MenuItem value="FIXED_COSTS">Fixed Costs</MenuItem>
                    </Select>
                  </FormControl>
                  <FormControl fullWidth size="small">
                    <InputLabel>Product family</InputLabel>
                    <Select label="Product family" value={editDraft.productFamily} onChange={(e) => mergeEditDraft({ productFamily: e.target.value })}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="valve_body">Valve Body</MenuItem>
                      <MenuItem value="o_rings">O-Rings</MenuItem>
                      <MenuItem value="valves">Valves</MenuItem>
                      <MenuItem value="solenoids">Solenoids</MenuItem>
                      <MenuItem value="sensors">Sensors</MenuItem>
                      <MenuItem value="springs">Springs</MenuItem>
                      <MenuItem value="clips">Clips</MenuItem>
                      <MenuItem value="retainers">Retainers</MenuItem>
                      <MenuItem value="checkballs">Checkballs</MenuItem>
                      <MenuItem value="bolts_nuts">Bolts & Nuts</MenuItem>
                      <MenuItem value="misc">Misc</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Stack>
            </IosCard>

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>Stock</Typography>
              <Stack spacing={0.9}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={editDraft.status}
                    onChange={(e) => mergeEditDraft({ status: e.target.value })}
                  >
                    {["available", "reserved", "consumed", "discontinued"].map((s) => (
                      <MenuItem key={s} value={s}>
                        {pretty(s)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  size="small"
                  label="Location"
                  value={editDraft.location}
                  onChange={(e) => mergeEditDraft({ location: e.target.value })}
                />

                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Quantity on hand"
                    type="number"
                    value={editDraft.quantityOnHand}
                    onChange={(e) => mergeEditDraft({ quantityOnHand: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Quantity reserved"
                    type="number"
                    value={editDraft.quantityReserved}
                    onChange={(e) => mergeEditDraft({ quantityReserved: e.target.value })}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <TextField fullWidth size="small" label="Lot number" value={editDraft.lotNumber} onChange={(e) => mergeEditDraft({ lotNumber: e.target.value })} />
                  <TextField fullWidth size="small" label="Serial number" value={editDraft.serialNumber} onChange={(e) => mergeEditDraft({ serialNumber: e.target.value })} />
                </Stack>
              </Stack>
            </IosCard>

            {String(editDraft.itemType || "").toUpperCase() !== "OVERHEAD" && String(editDraft.itemType || "").toUpperCase() !== "FIXED_COSTS" ? (
              <IosCard>
                <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>Shipping package</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <TextField fullWidth size="small" label="Weight (kg)" type="number" value={editDraft.shippingWeightKg} onChange={(e) => mergeEditDraft({ shippingWeightKg: e.target.value })} />
                  <TextField fullWidth size="small" label="Length (cm)" type="number" value={editDraft.packageLengthCm} onChange={(e) => mergeEditDraft({ packageLengthCm: e.target.value })} />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9} sx={{ mt: 0.9 }}>
                  <TextField fullWidth size="small" label="Width (cm)" type="number" value={editDraft.packageWidthCm} onChange={(e) => mergeEditDraft({ packageWidthCm: e.target.value })} />
                  <TextField fullWidth size="small" label="Height (cm)" type="number" value={editDraft.packageHeightCm} onChange={(e) => mergeEditDraft({ packageHeightCm: e.target.value })} />
                </Stack>
              </IosCard>
            ) : null}

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>Pricing</Typography>
              <Stack spacing={0.9}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit cost"
                    type="number"
                    value={editDraft.unitCost}
                    onChange={(e) => mergeEditDraft({ unitCost: e.target.value })}
                  />
                  <TextField
                    fullWidth
                    size="small"
                    label="Unit price"
                    type="number"
                    value={editDraft.unitPrice}
                    onChange={(e) => mergeEditDraft({ unitPrice: e.target.value })}
                  />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <TextField fullWidth size="small" label="Retail" type="number" value={editDraft.priceRetail} onChange={(e) => mergeEditDraft({ priceRetail: e.target.value })} />
                  <TextField fullWidth size="small" label="Wholesale" type="number" value={editDraft.priceWholesale} onChange={(e) => mergeEditDraft({ priceWholesale: e.target.value })} />
                  <TextField fullWidth size="small" label="Bulk" type="number" value={editDraft.priceBulk} onChange={(e) => mergeEditDraft({ priceBulk: e.target.value })} />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <TextField fullWidth size="small" label="Core cost" type="number" value={editDraft.coreCost} onChange={(e) => mergeEditDraft({ coreCost: e.target.value })} />
                  <TextField fullWidth size="small" label="Core charge" type="number" value={editDraft.coreCharge} onChange={(e) => mergeEditDraft({ coreCharge: e.target.value })} />
                </Stack>
              </Stack>
            </IosCard>

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>MX CFDI / SAT</Typography>
              <Stack spacing={0.9}>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <TextField fullWidth size="small" label="SAT product key" value={editDraft.satProductKey} onChange={(e) => mergeEditDraft({ satProductKey: e.target.value })} />
                  <TextField fullWidth size="small" label="SAT unit key" value={editDraft.satUnitKey} onChange={(e) => mergeEditDraft({ satUnitKey: e.target.value })} />
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={0.9}>
                  <TextField fullWidth size="small" label="SAT unit name" value={editDraft.satUnitName} onChange={(e) => mergeEditDraft({ satUnitName: e.target.value })} />
                  <FormControl fullWidth size="small">
                    <InputLabel>Tax object</InputLabel>
                    <Select label="Tax object" value={editDraft.taxObject} onChange={(e) => mergeEditDraft({ taxObject: e.target.value })}>
                      <MenuItem value="">None</MenuItem>
                      <MenuItem value="01">01 - Not subject to tax</MenuItem>
                      <MenuItem value="02">02 - Subject to tax</MenuItem>
                      <MenuItem value="03">03 - Subject to tax and no breakout</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
                <TextField
                  fullWidth
                  size="small"
                  label="MX taxes JSON"
                  multiline
                  minRows={3}
                  value={editDraft.mxTaxes}
                  onChange={(e) => mergeEditDraft({ mxTaxes: e.target.value })}
                />
              </Stack>
            </IosCard>

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>White label</Typography>
              <Stack spacing={0.9}>
                <FormControl fullWidth size="small">
                  <InputLabel>White label</InputLabel>
                  <Select label="White label" value={editDraft.isWhiteLabel ? "yes" : "no"} onChange={(e) => mergeEditDraft({ isWhiteLabel: e.target.value === "yes" })}>
                    <MenuItem value="no">No</MenuItem>
                    <MenuItem value="yes">Yes</MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  fullWidth
                  size="small"
                  label="White label name"
                  value={editDraft.whiteLabelName}
                  onChange={(e) => mergeEditDraft({ whiteLabelName: e.target.value })}
                />
              </Stack>
            </IosCard>

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>Images</Typography>
              <Stack spacing={0.8}>
                <Button component="label" variant="outlined" sx={secondaryActionSx} startIcon={<Icon>image</Icon>}>
                  Add Images
                  <input hidden type="file" accept="image/*" multiple onChange={(e) => setImageFiles(Array.from(e.target.files || []))} />
                </Button>
                {imageFiles.length ? (
                  <Typography sx={{ color: iosColors.muted, fontSize: 12.5, fontWeight: 850 }}>
                    {imageFiles.length} image{imageFiles.length === 1 ? "" : "s"} selected
                  </Typography>
                ) : null}
              </Stack>
            </IosCard>

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>Catalog text</Typography>
              <Stack spacing={0.9}>
                <TextField fullWidth size="small" label="Marketing description" multiline minRows={3} value={editDraft.marketingDescription} onChange={(e) => mergeEditDraft({ marketingDescription: e.target.value })} />
                <TextField fullWidth size="small" label="Unit-specific details" multiline minRows={3} value={editDraft.unitSpecificDetails} onChange={(e) => mergeEditDraft({ unitSpecificDetails: e.target.value })} />
              </Stack>
            </IosCard>

            <IosCard>
              <Typography sx={{ color: iosColors.ink, fontSize: 16, fontWeight: 900, mb: 0.9 }}>Notes</Typography>
              <TextField
                fullWidth
                size="small"
                label="Notes"
                multiline
                minRows={4}
                value={editDraft.notes}
                onChange={(e) => mergeEditDraft({ notes: e.target.value })}
              />
            </IosCard>
          </Stack>
        </Box>

        <Box sx={stickyFooterSx}>
          {updateItemMutation.isPending ? <LinearProgress sx={{ mb: 0.75, borderRadius: 1 }} /> : null}
          <Stack direction="row" spacing={0.75}>
            <Button variant="outlined" fullWidth onClick={() => setEditOpen(false)} sx={secondaryActionSx}>
              Cancel
            </Button>
            <Button
              variant="contained"
              fullWidth
              disabled={updateItemMutation.isPending || !editDraft.sku || !editDraft.name}
              sx={primaryActionSx}
              onClick={() => {
                const toNumOrNull = (v) => {
                  if (v === "" || v === null || v === undefined) return null;
                  const n = parseFloat(v);
                  return Number.isFinite(n) ? n : null;
                };
                let parsedMxTaxes = null;
                try {
                  parsedMxTaxes = String(editDraft.mxTaxes || "").trim() ? JSON.parse(editDraft.mxTaxes) : null;
                } catch {
                  setNotice({ type: "error", text: "MX taxes must be valid JSON. Example: [{\"type\":\"IVA\",\"rate\":0.16}]" });
                  return;
                }
                const priceLevelsPayload = {
                  ...(toNumOrNull(editDraft.priceRetail) != null ? { retail: toNumOrNull(editDraft.priceRetail) } : {}),
                  ...(toNumOrNull(editDraft.priceWholesale) != null ? { wholesale: toNumOrNull(editDraft.priceWholesale) } : {}),
                  ...(toNumOrNull(editDraft.priceBulk) != null ? { bulk: toNumOrNull(editDraft.priceBulk) } : {}),
                };
                setNotice(null);
                updateItemMutation.mutate({
                  sku: editDraft.sku || null,
                  name: editDraft.name || null,
                  description: editDraft.description || null,
                  itemType: editDraft.itemType || "FG",
                  productFamily: editDraft.productFamily || null,
                  status: editDraft.status || null,
                  location: editDraft.location || null,
                  quantityOnHand: toNumOrNull(editDraft.quantityOnHand),
                  quantityReserved: toNumOrNull(editDraft.quantityReserved),
                  lotNumber: editDraft.lotNumber || null,
                  serialNumber: editDraft.serialNumber || null,
                  shippingWeightKg: toNumOrNull(editDraft.shippingWeightKg),
                  packageLengthCm: toNumOrNull(editDraft.packageLengthCm),
                  packageWidthCm: toNumOrNull(editDraft.packageWidthCm),
                  packageHeightCm: toNumOrNull(editDraft.packageHeightCm),
                  unitCost: toNumOrNull(editDraft.unitCost),
                  unitPrice: toNumOrNull(editDraft.unitPrice),
                  coreCost: toNumOrNull(editDraft.coreCost),
                  coreCharge: toNumOrNull(editDraft.coreCharge),
                  priceLevels: Object.keys(priceLevelsPayload).length ? priceLevelsPayload : null,
                  satProductKey: editDraft.satProductKey || null,
                  satUnitKey: editDraft.satUnitKey || null,
                  satUnitName: editDraft.satUnitName || null,
                  taxObject: editDraft.taxObject || null,
                  mxTaxes: parsedMxTaxes,
                  isWhiteLabel: !!editDraft.isWhiteLabel,
                  whiteLabelName: editDraft.whiteLabelName || null,
                  marketingDescription: editDraft.marketingDescription || null,
                  unitSpecificDetails: editDraft.unitSpecificDetails || null,
                  notes: editDraft.notes || null,
                });
              }}
            >
              Save
            </Button>
          </Stack>
        </Box>
      </Dialog>
    </IosPage>
  );
}

function InfoLine({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="flex-start">
      <Typography sx={{ color: iosColors.muted, fontSize: 12.5, flex: "0 0 auto", maxWidth: "44%" }}>{label}</Typography>
      <Typography sx={{ color: iosColors.ink, fontSize: 12.5, fontWeight: 850, textAlign: "right", minWidth: 0, overflowWrap: "anywhere" }}>
        {value}
      </Typography>
    </Stack>
  );
}

InfoLine.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.node,
};

InfoLine.defaultProps = {
  value: "—",
};
